import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from 'react-redux'
import {getProjectsList,
  addProject
} from "../../actions/projectsActions"
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,

  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

// MUI (Material UI) components
import {
  Box,
  Grid,
  Stack,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,

  Tooltip,

} from "@mui/material";

import {
  ClearRounded,
  SaveRounded,
} from "@mui/icons-material";

import OverlayCard from "./OverlayCard";
import SortableProjectCard from "./SortableProjectCard";

// --- Utilities --------------------------------------------------------------
const STORAGE_KEY = "projects:order";

function loadOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveOrder(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {}
}

function applyOrder(items, order) {
  if (!order) return items;
  const map = new Map(items.map((it) => [it.id, it]));
  const ordered = order
    .map((id) => map.get(id))
    .filter(Boolean);
  const remaining = items.filter((it) => !order.includes(it.id));
  return [...ordered, ...remaining];
}

function useUniqueTags(items) {
  return useMemo(() => {
    const s = new Set();
    items.forEach((p) => p.tags?.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);
}
function useDialogState(initial = false) {
  const [open, setOpen] = useState(initial);
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  return { open, onOpen, onClose };
}

// --- Main Page --------------------------------------------------------------
export default function ProjectsPageMUI() {
  const dispatch = useDispatch()
  const projectsList = useSelector((state) => state.projectsList);

  const dlg = useDialogState();
  const [form, setForm] = useState({title: "", tags:[]} );


  const [projects, setProjects] = useState(() =>
    applyOrder(projectsList.data, loadOrder())
  );
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  function startNew() {
    setForm({title: "", tags:[]});
    dlg.onOpen();
  }
  function startEdit(newForm) {
    setForm(newForm);
    dlg.onOpen();
  }

  function submit() {
    let parsedTags = typeof(form.tags)==="string"?
    form.tags.split(","):
    []
    console.log(parsedTags)
    dispatch(addProject({...form,
      tags: parsedTags
    }
    ))
    dlg.onClose();
  }

  const tags = useUniqueTags(projects);
  useEffect(() => {  
    dispatch(getProjectsList())
  }, []);  

  // Persist order
  useEffect(() => {
    saveOrder(projects.map((p) => p.id));
  }, [projects]);

  useEffect(() => {
    setProjects(applyOrder(projectsList.data, loadOrder()));
  }, [projectsList.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      const matchesQ = !q
        ? true
        : p.title.toLowerCase().includes(q) ||
          // p.description.toLowerCase().includes(q) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(q));
      const matchesTag = !tag ? true : p.tags?.includes(tag);
      return matchesQ && matchesTag;
    });
  }, [projects, query, tag]);

  function handleDragStart(evt) {
    setActiveId(evt.active.id);
  }

  function handleDragEnd(evt) {
    const { active, over } = evt;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    setProjects((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  function resetOrder() {
    localStorage.removeItem(STORAGE_KEY);
    setProjects(projectsList.data);
  }

  const activeProject = projects.find((p) => p.id === activeId);

  return (
<>
    <Box sx={{ maxWidth: 1200, mx: "auto", px: 2, py: 4 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} letterSpacing={0.2}>
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag cards to reorder. Search and filter by tag.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} 
        sx={{ width: { xs: "100%", sm: "auto" } }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search projects…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="tag-label">Tag</InputLabel>
            <Select
              labelId="tag-label"
              value={tag}
              label="Tag"
              onChange={(e) => setTag(e.target.value)}
            >
              <MenuItem value=""><em>All tags</em></MenuItem>
              {tags.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => { setQuery(""); setTag(""); }}>
              Clear
            </Button>
            <Button variant="contained" onClick={resetOrder}>
              Reset order
            </Button>
            <Button variant="contained"  onClick={() => {
                    startEdit(form)
                  }
                  }>
              Add Project
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* DND Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={filtered.map((p) => p.id)} strategy={rectSortingStrategy}>
          <Grid container spacing={2}>
            {filtered.map((p) => (
              <Grid key={p.id}  size={{xs:12, sm:6, lg:4}}>
                <SortableProjectCard project={p} activeId={activeId} />
              </Grid>
            ))}
          </Grid>
        </SortableContext>

        <DragOverlay dropAnimation={{ duration: 150, easing: "ease-out" }}>
          <OverlayCard project={activeProject} />
        </DragOverlay>
      </DndContext>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 4 }}>
        Tip: The order is saved in your browser (localStorage).
      </Typography>
     
    </Box>
     <Dialog open={dlg.open} onClose={dlg.onClose} fullWidth maxWidth="sm">
     <DialogTitle>{"Rename Project"}</DialogTitle>
     <DialogContent>
       <Stack spacing={2} sx={{ mt: 1 }}>
         <TextField label="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value })} autoFocus />
       </Stack>
       <Stack spacing={2} sx={{ mt: 1 }}>
         <TextField label="Tags" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value })} autoFocus />
       </Stack>
     </DialogContent>
     <DialogActions>
       <Button startIcon={<ClearRounded />} onClick={dlg.onClose}>Cancel</Button>
       <Button startIcon={<SaveRounded />} variant="contained" onClick={submit}>
         Save
       </Button>
     </DialogActions>
   </Dialog>
   </>
  );
}
